import cx from 'clsx';
import * as Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { Tooltip } from 'react-tooltip';

import { ReactComponent as FilterIcon } from '@assets/svg/filter.svg';

import ResetButton from '@components/common/ResetButton/ResetButton';
import SkeletonBlock from '@components/common/Skeleton/SkeletonBlock';

import useTranslation from '@hooks/useTranslation';

import { isQueryEnabled } from '@pages/SiteEngagement/helpers/isQueryEnabled';
import { useEngagementStatsFacilityQuery } from '@pages/SiteEngagement/services/useEngagementStatsFacilityQuery';

import { selectAppLocationSelectValue } from '@store/useAppStore';

import {
  type IFormSiteEngagementMetricsFilters,
  schemaSiteEngagementMetricsFilters,
  siteEngagementMetricsFilters,
} from '../../constants/schemaSiteEngagementMetricsFilters';
import {
  FACILITY_NAME_TOOLTIP_ID,
  useGetSiteEngagementOptions,
} from '../../hooks/useGetSiteEngagementMetricsOptions';
import {
  selectSiteEngagementMetricsFilterStorePersistState,
  useSiteEngagementMetricsFilterStorePersistActions,
} from '../../store/useSiteEngagementMetricsFilterStorePersist';
import NoData from '../NoData/NoData';
import SiteEngagementMetricsFilters from '../SiteEngagementMetricsFilters/SiteEngagementMetricsFilters';
import StatisticWrapper from '../StatisticWrapper/StatisticWrapper';
import style from './style.module.scss';

const SiteEngagementMetrics = () => {
  const { customerId } = useParams();

  const { t } = useTranslation();

  const { setSiteEngagementMetricsFilterState, setSiteEngagementMetricsFilterInitialState } =
    useSiteEngagementMetricsFilterStorePersistActions();

  const [showFilter, setShowFilter] = useState(false);

  const locationSelectValue = selectAppLocationSelectValue();
  const filter = selectSiteEngagementMetricsFilterStorePersistState();

  const { data, isLoading, isRefetching } = useEngagementStatsFacilityQuery({
    filter,
    locationIds:
      locationSelectValue.level_name === 'allFacilities' ? undefined : locationSelectValue?.filterIds,
    enabled: isQueryEnabled(filter),
  });

  const { hiddenOptions, options } = useGetSiteEngagementOptions(data);

  const formattedTimeCreatedRange =
    filter.minTimeCreated && filter.maxTimeCreated
      ? [new Date(filter.minTimeCreated), new Date(filter.maxTimeCreated)]
      : [];

  const formValues = {
    facilityIds: filter.facilityIds,
    metricType: filter.metricType,
    reportedFailure: filter.reportedFailure,
    type: filter.type,
    urgency: filter.urgency,
    timeCreatedRange: formattedTimeCreatedRange,
  };

  const {
    control,
    handleSubmit,
    reset,
    formState: { isValid, isDirty },
  } = useForm<IFormSiteEngagementMetricsFilters>({
    mode: 'all',
    defaultValues: siteEngagementMetricsFilters,
    resolver: schemaSiteEngagementMetricsFilters,
    values: {
      ...formValues,
    },
    resetOptions: { keepDefaultValues: true },
  });

  const handleReset = () => {
    reset();
    setSiteEngagementMetricsFilterInitialState();
  };

  const handleSubmitForm = handleSubmit((formData) => {
    const [minTimeCreated, maxTimeCreated] = formData.timeCreatedRange;

    const updatedFilterValues = {
      facilityIds: formData.facilityIds,
      metricType: formData.metricType,
      reportedFailure: formData.reportedFailure,
      type: formData.type,
      urgency: formData.urgency,
      minTimeCreated,
      maxTimeCreated,
    };

    setSiteEngagementMetricsFilterState({ ...updatedFilterValues });

    setShowFilter(false);
  });

  const handleCloseFilter = () => {
    reset(
      {
        ...formValues,
      },
      { keepDefaultValues: true },
    );
    setShowFilter(false);
  };
  return (
    <div className={style.siteEngagementMetricsWrapper}>
      <div className={style.hiddenSiteEngagementMetricsStatisticWrapper}>
        <StatisticWrapper
          title={t('Site Engagement Metrics')}
          link={`/customer/${customerId}/reports/site-engagement`}
          rightElement={
            <div className={style.filterIconWrapper} onClick={() => setShowFilter(true)}>
              <FilterIcon className={style.filterIcon} />
            </div>
          }
          linkText="View Report"
        >
          <div className={style.siteEngagementMetricsChartWrapper}>
            <HighchartsReact
              highcharts={Highcharts}
              options={hiddenOptions}
              containerProps={{
                className: cx(style.siteEngagementMetricsChart, 'printHighchartsChart'),
              }}
            />
          </div>
        </StatisticWrapper>
      </div>
      <div className={style.siteEngagementMetricsStatisticWrapper}>
        {showFilter ? (
          <SiteEngagementMetricsFilters
            data={data}
            control={control}
            isDirty={isDirty}
            isValid={isValid}
            handleReset={handleReset}
            handleSubmitForm={handleSubmitForm}
            handleCloseFilter={handleCloseFilter}
          />
        ) : (
          <StatisticWrapper
            title={t('Site Engagement Metrics')}
            link={`/customer/${customerId}/reports/site-engagement`}
            rightElement={
              <div className={style.rightElementsWrapper}>
                <div
                  className={cx(style.filterIconWrapper, {
                    [style.activeFilterIconWrapper]: isDirty,
                  })}
                  onClick={() => setShowFilter(true)}
                  id="engagement-metrics-apply-filters-tooltip"
                >
                  <FilterIcon className={style.filterIcon} />
                </div>
                <div className={style.resetWrapper}>
                  <ResetButton showResetButton={isDirty} onClick={handleReset} />
                </div>
                <Tooltip
                  anchorSelect="#engagement-metrics-apply-filters-tooltip"
                  content={t('Apply Filters')}
                />
              </div>
            }
            linkText="View Report"
          >
            <div className={style.siteEngagementMetricsChartWrapper}>
              {(isLoading || isRefetching) && <SkeletonBlock className={style.skeleton} />}
              {!isLoading && !isRefetching && !!data?.facilities.length && (
                <HighchartsReact
                  highcharts={Highcharts}
                  options={options}
                  containerProps={{
                    className: style.siteEngagementMetricsChart,
                  }}
                />
              )}
              {!isLoading && !isRefetching && !data?.facilities.length && <NoData />}
            </div>
          </StatisticWrapper>
        )}
      </div>
      <Tooltip id={FACILITY_NAME_TOOLTIP_ID} />
    </div>
  );
};

export default SiteEngagementMetrics;
